import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PrintIcons, DownArrowIcon } from '../../assets/svg/icons';
import { APP_TIMEZONE } from '../../utils/constants/formInitialStates';
import { useCustomDispatch } from '../../hooks';
import { useAccountibilitySelector } from '../../services/selectors/accountabilitySelectors';

import {
    GenerateLabelsByRxDateRange,
    SearchPharmacyRx,
    ClearPharmacyRxSearch
} from '../../services/reducers/pharmacy/accountibility/accountibilityNewRx';

import * as Common from '../../components/common';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

const PrintSingleRxButton = () => {

    const { printLabelsResponse, searchPharmacyRxResponse } = useAccountibilitySelector();
    const dispatch = useCustomDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [rxNumber, setRxNumber] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });
    
    const dateRangeRef = useRef(null);
    const flatpickrInstance = useRef(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Search results from API
    const searchResults = searchPharmacyRxResponse?.data?.data || [];
    const isSearching = searchPharmacyRxResponse?.loading;

    // Helper to get current date in CA timezone
    const getCurrentDate = () => {
        return new Intl.DateTimeFormat("en-CA", {
            timeZone: APP_TIMEZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).format(new Date());
    };

    // Helper to get tomorrow's date in CA timezone
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return new Intl.DateTimeFormat("en-CA", {
            timeZone: APP_TIMEZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).format(tomorrow);
    };

    // Debounced search function
    const debouncedSearch = useCallback((searchValue) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchValue.trim().length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                dispatch(SearchPharmacyRx({
                    search: searchValue.trim(),
                    fetchType: 'pharmacy-rx-search'
                }));
                setShowDropdown(true);
            }, 300);
        } else {
            dispatch(ClearPharmacyRxSearch());
            setShowDropdown(false);
        }
    }, [dispatch]);

    // Handle RX number input change
    const handleRxNumberChange = (e) => {
        const value = e.target.value;
        setRxNumber(value);
        debouncedSearch(value);
    };

    // Handle selecting an RX from dropdown
    const handleSelectRx = (rx) => {
        setRxNumber(rx.rxnum);
        setShowDropdown(false);
        dispatch(ClearPharmacyRxSearch());
    };

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Initialize flatpickr date range picker
    useEffect(() => {
        if (isOpen && dateRangeRef.current) {
            // Destroy existing instance if it exists
            if (flatpickrInstance.current) {
                flatpickrInstance.current.destroy();
            }

            const today = getCurrentDate();
            const tomorrow = getTomorrowDate();

            // Convert date strings to Date objects for flatpickr
            const todayDate = new Date(today + 'T00:00:00');
            const tomorrowDate = new Date(tomorrow + 'T00:00:00');

            // Initialize flatpickr with range mode
            flatpickrInstance.current = flatpickr(dateRangeRef.current, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                minDate: 'today',
                defaultDate: [todayDate, tomorrowDate],
                onChange: function (selectedDates, dateStr, instance) {
                    if (selectedDates.length === 2) {
                        const startDate = selectedDates[0];
                        const endDate = selectedDates[1];

                        // Format dates in CA timezone
                        const formatDate = (date) => {
                            return new Intl.DateTimeFormat("en-CA", {
                                timeZone: APP_TIMEZONE,
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit"
                            }).format(date);
                        };

                        setDateRange({
                            startDate: formatDate(startDate),
                            endDate: formatDate(endDate)
                        });
                    } else if (selectedDates.length === 1) {
                        // User is still selecting, only start date is selected
                        const formatDate = (date) => {
                            return new Intl.DateTimeFormat("en-CA", {
                                timeZone: APP_TIMEZONE,
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit"
                            }).format(date);
                        };
                        setDateRange({
                            startDate: formatDate(selectedDates[0]),
                            endDate: null
                        });
                    }
                }
            });

            // Set initial date range
            setDateRange({
                startDate: today,
                endDate: tomorrow
            });
            setRxNumber('');
        }

        // Cleanup on unmount or when closed
        return () => {
            if (flatpickrInstance.current) {
                flatpickrInstance.current.destroy();
                flatpickrInstance.current = null;
            }
        };
    }, [isOpen]);


    // Format date as "DD MMM" (e.g., "05 Jan") for API
    const formatDateForAPI = (dateStr) => {
        if (!dateStr) return null;

        // dateStr is in YYYY-MM-DD format from flatpickr
        const dateObj = new Date(dateStr + 'T00:00:00');

        // Format as "DD MMM" using UTC to avoid timezone conversion
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getUTCMonth()];

        return `${day} ${month}`;
    };

    const handlePrint = () => {
        if (!rxNumber.trim()) {
            return;
        }
        if (!dateRange.startDate || !dateRange.endDate) {
            return;
        }

        const startDate = formatDateForAPI(dateRange.startDate);
        const endDate = formatDateForAPI(dateRange.endDate);

        if (!startDate || !endDate) {
            return;
        }

        const payload = {
            rx_number: rxNumber.trim(),
            start_date: startDate,
            end_date: endDate
        };

        dispatch(GenerateLabelsByRxDateRange({
            payload,
            fetchType: `generateLabels-single-${rxNumber.trim()}`
        }));

        // Close modal after successful API call (handled in reducer)
        // The reducer will handle showing toast and previewing PDF
    };

    const toggleInterface = () => {
        setIsOpen(prev => !prev);
    };

    const handleClose = () => {
        setRxNumber('');
        setDateRange({
            startDate: null,
            endDate: null
        });
        if (dateRangeRef.current) {
            dateRangeRef.current.value = '';
        }
        setShowDropdown(false);
        dispatch(ClearPharmacyRxSearch());
        setIsOpen(false);
    };

    // Keyboard shortcut: F8 to toggle popup
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Check if F8 key is pressed
            if (event.key === 'F1') {
                event.preventDefault();
                toggleInterface();
            }
        };

        // Add event listener
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []); // Empty dependency array is fine since toggleInterface uses functional update

    // Cleanup search timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="relative hidden lg:block">
            {/* Chat-like Interface */}
            {isOpen && (
                <section
                    className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-2xl z-[999999] max-h-[500px]">
                    {/* Header */}
                    <div className="bg-[#4E64C2] text-white font-medium rounded-t-lg border-b-0">
                        <div className="h-[52px] flex items-center justify-between px-4">
                            <h3 title="Print Label" className="flex items-center gap-3 text-white font-medium text-lg">
                                <div className="[&_svg]:stroke-white [&_svg]:w-5 [&_svg]:h-5">
                                    <PrintIcons />
                                </div>
                                Print Label
                            </h3>
                            <button
                                className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={toggleInterface}
                                aria-label="Toggle Print Label"
                            >
                                <DownArrowIcon />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="w-full rounded-b-lg max-h-[400px] overflow-y-auto">
                        <div className="p-6 space-y-6">
                            {/* RX Number Input with Search Dropdown */}
                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    RX Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={rxNumber}
                                        onChange={handleRxNumberChange}
                                        onFocus={() => {
                                            if (searchResults.length > 0) {
                                                setShowDropdown(true);
                                            }
                                        }}
                                        placeholder="Enter RX Number"
                                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm hover:border-gray-400"
                                        autoFocus
                                        autoComplete="off"
                                    />
                                    {/* Loading indicator inside input */}
                                    {isSearching && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                </div>

                                {/* Search Dropdown */}
                                {showDropdown && !isSearching && (
                                    <div
                                        ref={dropdownRef}
                                        className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                                    >
                                        {searchResults.length > 0 ? (
                                            <ul className="py-1">
                                                {searchResults.map((rx, index) => (
                                                    <li
                                                        key={rx.id || index}
                                                        onClick={() => handleSelectRx(rx)}
                                                        className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-gray-800">{rx.rxnum}</span>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rx.rxstat === 'Y'
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                STAT: {rx.rxstat || ''}
                                                            </span>

                                                        </div>
                                                        {(rx.ai_start_date || rx.ai_stop_date) && (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Duration: {rx.ai_start_date} - {rx.ai_stop_date}
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : rxNumber.trim().length >= 2 ? (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No RX found matching "{rxNumber}"
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>

                            {/* Date Range Picker */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Date Range <span className="text-red-500">*</span>
                                </label>
                                <input
                                    ref={dateRangeRef}
                                    type="text"
                                    placeholder="Select date range"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm hover:border-gray-400 cursor-pointer bg-white"
                                    readOnly
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                                <button
                                    onClick={handleClose}
                                    className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePrint}
                                    disabled={!rxNumber.trim() || !dateRange.startDate || !dateRange.endDate || (printLabelsResponse?.loading && printLabelsResponse?.fetchType === `generateLabels-single-${rxNumber.trim()}`)}
                                    className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                >
                                    {printLabelsResponse?.loading && printLabelsResponse?.fetchType === `generateLabels-single-${rxNumber.trim()}` ? (
                                        <>
                                            <Common.Loader color='white' />
                                            <span>Printing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                                            </svg>
                                            <span>Print Label</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Inline trigger button */}
            <button
                onClick={toggleInterface}
                className="h-12 min-w-[56px] bg-[#4E64C2] rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors inline-flex items-center justify-center"
                type="button"
                aria-label="Print Label"
            >
                F1
            </button>
        </div>
    );
};

export default PrintSingleRxButton;
